import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Camera, Eye, EyeOff, Plus, Trash2, X } from 'lucide-react';
import ImageCropDialog from './ImageCropDialog';
import { staffApi, ProfileResponse, StaffProfile, Certification, TONE_CLASS } from './staffApi';
import CoachCard from '../../app/member/components/CoachCard';
import '../../app/member/design/member.css';

const GENDERS = [
  { value: '', label: 'Not shown' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
];

/**
 * Who a provider is, as members see them in the app and at checkout. The
 * right-hand preview is the member app's own card component, fed from this
 * form as you type.
 */
export default function StaffProfileTab({ mappingId, onSaved }: { mappingId: string; onSaved?: () => void }) {
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [form, setForm] = useState<StaffProfile | null>(null);
  const [nextFree, setNextFree] = useState<{ date: string; startTime: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [crop, setCrop] = useState<{ src: string; kind: 'photo' | 'cover' } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    staffApi<ProfileResponse>(`/${mappingId}/profile`)
      .then(r => { if (live) { setData(r); setForm(r.profile); } })
      .catch(e => toast.error(e.message));
    staffApi<any>(`/${mappingId}/preview`).then(r => { if (live) setNextFree(r?.nextFree ?? null); }).catch(() => {});
    return () => { live = false; };
  }, [mappingId]);

  const dirty = useMemo(() => !!data && !!form && JSON.stringify(data.profile) !== JSON.stringify(form), [data, form]);
  if (!data || !form) return <div className="p-8 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-12 rounded-xl bg-zinc-100 animate-pulse" />)}</div>;

  const set = <K extends keyof StaffProfile>(k: K, v: StaffProfile[K]) => setForm(f => (f ? { ...f, [k]: v } : f));
  const level = data.levels.find(l => l.id === form.levelId) ?? null;

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        headline: form.headline, bio: form.bio, specialties: form.specialties, languages: form.languages,
        certifications: form.certifications, experienceYears: form.experienceYears, gender: form.gender, introVideoUrl: form.introVideoUrl,
      };
      if (data.can.editLevel) body.levelId = form.levelId;
      if (data.can.editVisibility) { body.isPublic = form.isPublic; body.sortOrder = form.sortOrder; }
      const saved = await staffApi<StaffProfile>(`/${mappingId}/profile`, { method: 'PUT', body });
      setData(d => (d ? { ...d, profile: saved } : d));
      setForm(saved);
      toast.success('Profile saved');
      onSaved?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const pickFile = (kind: 'photo' | 'cover') => {
    if (!fileRef.current) return;
    fileRef.current.dataset.kind = kind;
    fileRef.current.click();
  };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const kind = (e.target.dataset.kind as 'photo' | 'cover') || 'photo';
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCrop({ src: String(reader.result), kind });
    reader.readAsDataURL(file);
  };
  const upload = async (blob: Blob) => {
    if (!crop) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, `${crop.kind}.jpg`);
      const r = await staffApi<any>(`/${mappingId}/photo?kind=${crop.kind}`, { method: 'POST', form: fd });
      const field = crop.kind === 'cover' ? 'coverUrl' : 'photoUrl';
      setData(d => (d ? { ...d, profile: { ...d.profile, [field]: r[field] } } : d));
      setForm(f => (f ? { ...f, [field]: r[field] } : f));
      setCrop(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 p-6">
      <div className="space-y-6 min-w-0">
        {/* Photo + visibility */}
        <div className="flex items-start gap-5">
          <button onClick={() => pickFile('photo')} className="relative w-24 h-[120px] rounded-2xl overflow-hidden bg-zinc-100 flex items-center justify-center group shrink-0" title="Change photo">
            {form.photoUrl ? <img src={form.photoUrl} alt="" className="w-full h-full object-cover" /> : <span className="text-3xl font-black text-zinc-300">{form.name.charAt(0)}</span>}
            <span className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider">
              <Camera className="w-5 h-5" /> {form.photoUrl ? 'Change' : 'Add photo'}
            </span>
          </button>
          <div className="flex-1 space-y-3 min-w-0">
            <div>
              <div className="text-lg font-black text-zinc-900 truncate">{form.name}</div>
              <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest">{form.role}</div>
            </div>
            {data.can.editVisibility && (
              <button
                onClick={() => set('isPublic', !form.isPublic)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${form.isPublic ? 'bg-lime-50 border-lime-300 text-lime-900' : 'bg-zinc-50 border-zinc-200 text-zinc-500'}`}
              >
                {form.isPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {form.isPublic ? 'Shown in member app & checkout' : 'Hidden from members'}
              </button>
            )}
            {form.isPublic && !form.photoUrl && <p className="text-[11px] text-amber-700">Add a photo — the member card is built around it.</p>}
          </div>
        </div>

        <Field label="Headline" hint={`${form.headline.length}/80 · e.g. Strength & mobility coach`}>
          <input className={INPUT} maxLength={80} value={form.headline} onChange={e => set('headline', e.target.value)} placeholder="What you're known for, in one line" />
        </Field>

        <Field label="Bio" hint="Shown when a member flips the card. Two or three short paragraphs work best.">
          <textarea className={`${INPUT} min-h-[120px]`} maxLength={2000} value={form.bio} onChange={e => set('bio', e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Level">
            <select className={INPUT} value={form.levelId ?? ''} disabled={!data.can.editLevel} onChange={e => set('levelId', e.target.value || null)}>
              <option value="">No level</option>
              {data.levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            {data.levels.length === 0 && <p className="text-[11px] text-zinc-400 mt-1">Add levels for this role from Team → Levels.</p>}
          </Field>
          <Field label="Years of experience">
            <input className={INPUT} type="number" min={0} max={60} value={form.experienceYears ?? ''} onChange={e => set('experienceYears', e.target.value === '' ? null : Number(e.target.value))} />
          </Field>
        </div>

        <Field label="Specialties" hint="Members filter coaches by these.">
          <TagInput value={form.specialties} onChange={v => set('specialties', v)} suggestions={data.suggestions.specialties} placeholder="e.g. Strength, Prenatal, Deep tissue" max={12} />
        </Field>

        <Field label="Languages">
          <TagInput value={form.languages} onChange={v => set('languages', v)} suggestions={[...new Set(['English', 'Hindi', ...data.suggestions.languages])]} placeholder="e.g. English, Hindi, Tamil" max={8} />
        </Field>

        <Field label="Certifications">
          <CertEditor value={form.certifications} onChange={v => set('certifications', v)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.settings.showGender ? (
            <Field label="Gender" hint="Members can filter by this — your gym turned it on.">
              <select className={INPUT} value={form.gender ?? ''} onChange={e => set('gender', e.target.value || null)}>
                {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </Field>
          ) : <div />}
          {data.can.editVisibility && (
            <Field label="Order in lists" hint="Lower comes first.">
              <input className={INPUT} type="number" value={form.sortOrder} onChange={e => set('sortOrder', Number(e.target.value) || 0)} />
            </Field>
          )}
        </div>

        <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white/90 backdrop-blur border-t border-zinc-100 flex justify-end gap-2">
          <button onClick={() => setForm(data.profile)} disabled={!dirty || saving} className="px-4 py-2.5 rounded-xl text-sm font-bold text-zinc-600 hover:bg-zinc-100 disabled:opacity-40">Discard</button>
          <button onClick={save} disabled={!dirty || saving} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-black disabled:opacity-40">
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>

      {/* Live preview — the member app's own card */}
      <div className="lg:sticky lg:top-4 self-start space-y-2">
        <div className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.18em]">Member preview · checkout</div>
        <div className="mapp rounded-3xl p-4" style={{ minHeight: 0 }}>
          <CoachCard
            coach={{
              id: form.userId,
              name: form.name,
              photoUrl: form.photoUrl,
              headline: form.headline || null,
              bio: form.bio || null,
              specialties: form.specialties,
              certifications: form.certifications,
              experienceYears: form.experienceYears,
              languages: form.languages,
              level: level ? { name: level.name, tone: level.tone } : null,
              nextFree,
            }}
            onSelect={() => {}}
          />
        </div>
        {level && <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${TONE_CLASS[level.tone] ?? TONE_CLASS.ink}`}>Level: {level.name}</span>}
        {!form.isPublic && <p className="text-[11px] text-zinc-500">Members won’t see this card until you switch on “Shown in member app & checkout”.</p>}
      </div>

      <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onFile} />
      {crop && (
        <ImageCropDialog
          src={crop.src}
          aspect={crop.kind === 'cover' ? 16 / 9 : 4 / 5}
          title={crop.kind === 'cover' ? 'Crop cover image' : 'Crop photo — the member card is portrait'}
          busy={uploading}
          onCancel={() => setCrop(null)}
          onConfirm={upload}
        />
      )}
    </div>
  );
}

const INPUT = 'w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm bg-white outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 disabled:bg-zinc-50 disabled:text-zinc-400';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-black text-zinc-500 uppercase tracking-[0.16em] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-zinc-400 mt-1">{hint}</span>}
    </label>
  );
}

function TagInput({ value, onChange, suggestions, placeholder, max }: { value: string[]; onChange: (v: string[]) => void; suggestions: string[]; placeholder: string; max: number }) {
  const [draft, setDraft] = useState('');
  const add = (t: string) => {
    const tag = t.trim();
    if (!tag || value.some(v => v.toLowerCase() === tag.toLowerCase()) || value.length >= max) return;
    onChange([...value, tag]);
    setDraft('');
  };
  const open = suggestions.filter(s => !value.includes(s) && s.toLowerCase().includes(draft.toLowerCase())).slice(0, 8);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-xl bg-white focus-within:border-zinc-900">
        {value.map(t => (
          <span key={t} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-zinc-900 text-white text-xs font-bold">
            {t}
            <button type="button" onClick={() => onChange(value.filter(v => v !== t))} className="p-0.5 rounded hover:bg-white/20" aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
            if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => draft && add(draft)}
          placeholder={value.length ? '' : placeholder}
          className="flex-1 min-w-[120px] text-sm outline-none py-1 px-1"
        />
      </div>
      {open.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {open.map(s => (
            <button key={s} type="button" onClick={() => add(s)} className="px-2.5 py-1 rounded-lg border border-dashed border-zinc-300 text-xs font-semibold text-zinc-600 hover:border-zinc-900 hover:text-zinc-900">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CertEditor({ value, onChange }: { value: Certification[]; onChange: (v: Certification[]) => void }) {
  const update = (i: number, patch: Partial<Certification>) => onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-2">
      {value.map((c, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_84px_auto] gap-2">
          <input className={INPUT} placeholder="Certification" value={c.name} onChange={e => update(i, { name: e.target.value })} />
          <input className={INPUT} placeholder="Issuer (optional)" value={c.issuer ?? ''} onChange={e => update(i, { issuer: e.target.value })} />
          <input className={INPUT} placeholder="Year" type="number" value={c.year ?? ''} onChange={e => update(i, { year: e.target.value ? Number(e.target.value) : null })} />
          <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="p-2 text-zinc-400 hover:text-red-500" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {value.length < 12 && (
        <button type="button" onClick={() => onChange([...value, { name: '', issuer: '', year: null }])} className="inline-flex items-center gap-1 text-sm font-bold text-zinc-700 hover:text-zinc-900">
          <Plus className="w-4 h-4" /> Add certification
        </button>
      )}
    </div>
  );
}
